/**
 * 공용 대상 다중 선택 드롭다운 — AI 시나리오 화면의 "적용 대상을 고른다" 시각 언어를 통일한다.
 *
 * 생산 공정 상세처럼 후보 건수가 마스터에 따라 수백 건이 될 수 있는 목록은 칩으로 전부 깔 수 없다.
 * 검색해서 여러 건을 한 번에 고르고, 고른 것만 아래에 행으로 보여주는 방식으로 맞춘다.
 *
 * 팝업 셸·행 룩은 전역 테마(index.js 의 MuiAutocomplete)가, 그룹 헤더·검색어 하이라이트·체크박스는
 * AppAutocomplete 가 담당한다. 여기서 더하는 것은 AI 화면 밀도(FONT.CAPTION)뿐이다.
 *
 * 선택만 담당하고 선택된 행 렌더링은 호출부에 맡긴다 — 행 내용이 용도마다 다르기 때문이다
 * (작업 전환 시간은 설비별 수치 입력, 주기 캘린더는 캘린더명 + 확인 링크).
 *
 * @param {object[]} options       { key, label, group? } — AppAutocomplete 형식 그대로
 * @param {object[]} value         선택된 option 배열
 * @param {Function} onChange      (nextOptions) => void
 * @param {string}   placeholder
 * @param {string}   noOptionsText 후보가 비었을 때 문구
 * @param {boolean}  disabled
 * @param {object}   sx            배치용 오버라이드
 */
import { Fragment, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import AppAutocomplete from "@plannel/components/ui/AppAutocomplete";
import { C } from "@plannel/components/ai/aiTokens";
import { FONT } from "@plannel/components/ai/typographyTokens";

// 정렬 비교기는 한 번만 만든다 — String#localeCompare 는 호출마다 Collator 를 새로 만든다.
const COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

export function AiTargetMultiSelect({
  options, value, onChange, placeholder, noOptionsText, disabled, sx,
}) {
  // 검색어를 직접 쥔다 — MUI 는 항목을 고르거나 포커스가 빠질 때 입력칸을 비운다(reason="reset").
  //   여러 건을 연달아 고르는 것이 이 컴포넌트의 목적이라 그 초기화를 무시하고, 지우는 것은 X 로만 한다.
  const [keyword, setKeyword] = useState("");

  // 이름 오름차순. 그룹이 있으면 그룹 먼저 — MUI groupBy 는 정렬된 목록을 전제라
  //   섞이면 같은 그룹 헤더가 여러 번 나온다.
  //   시나리오 카드는 입력 한 글자마다 통째로 다시 그려진다 — 정렬을 렌더마다 돌리면
  //   그 비용이 타이핑 지연에 그대로 얹힌다(localeCompare 는 특히 비싸다).
  const sorted = useMemo(() => [...(options ?? [])].sort(
    (a, b) => COLLATOR.compare(a.group || "", b.group || "")
              || COLLATOR.compare(a.label || "", b.label || "")), [options]);

  return (
    <AppAutocomplete
      multiple
      disableCloseOnSelect
      openOnFocus
      options={sorted}
      value={value ?? []}
      // X 는 검색어만 지운다 — 고른 것은 아래 행으로 보이고 입력칸에는 칩이 없어서, MUI 기본값대로
      //   선택을 통째로 비우면 '검색 지우기'로 보이는 버튼이 목록을 날리는 셈이 된다(reason="clear"
      //   는 값 변경을 무시하고, 같은 이벤트의 onInputChange 가 검색어를 비운다).
      onChange={(_e, next, reason) => { if (reason !== "clear") onChange(next ?? []); }}
      inputValue={keyword}
      // reason="reset" 은 항목 선택·포커스 아웃 때 MUI 가 입력을 비우려는 신호다 — 둘 다 무시한다.
      //   검색어는 X 로만 지운다. 입력칸에 칩을 쌓지 않아 남은 검색어가 그대로 보이므로,
      //   다음에 열었을 때 후보가 줄어 있는 이유를 사용자가 눈으로 확인할 수 있다.
      onInputChange={(_e, next, reason) => { if (reason !== "reset") setKeyword(next); }}
      disabled={disabled}
      placeholder={placeholder}
      noOptionsText={noOptionsText}
      // 고른 것은 아래 행으로 보여주므로 입력칸 안에 칩을 쌓지 않는다(칸이 세로로 자라 레이아웃이 흔들린다).
      renderTags={() => null}
      // AppAutocomplete 의 renderOption 을 덮어쓴다({...rest} 를 마지막에 펼치므로 호출부가 이긴다).
      //   다중 선택은 선택 표시가 배경색뿐이라 무엇을 골랐는지 목록에서 읽기 어려워 체크박스를 앞에 둔다.
      //   아이콘·검색어 하이라이트는 원본과 같은 규격을 유지한다(공용 컴포넌트는 손대지 않는다).
      renderOption={(props, option, { inputValue, selected }) => {
        const parts = parse(option.label, match(option.label, inputValue ?? "", { insideWords: true }));
        return (
          <Box component="li" {...props} key={option.key}
            sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Checkbox size="small" checked={selected} disableRipple
              sx={{ p: 0, flexShrink: 0, "& svg": { fontSize: "16px" } }} />
            {option.icon && (
              <Box sx={{ display: "flex", flexShrink: 0 }}>{option.icon}</Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden",
                       textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {parts.map((part, idx) => part.highlight
                ? <Box key={idx} component="span" sx={{ color: "primary.main", fontWeight: 700 }}>{part.text}</Box>
                : <Fragment key={idx}>{part.text}</Fragment>)}
            </Box>
          </Box>
        );
      }}
      textFieldProps={{ sx: { "& .MuiOutlinedInput-root": { py: "1px" } } }}
      sx={{ "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
              p: "2px 4px", fontSize: FONT.CAPTION },
            // 입력칸은 늘 흰 바탕 - 투명이면 조정된 파라미터 행의 accent 틴트가 비쳐 입력칸이 물든다.
            "& .MuiOutlinedInput-root": { bgcolor: C.bg2 },
            "& fieldset": { borderColor: C.border },
            ...sx }}
      ListboxProps={{ sx: { maxHeight: 240,
            "& .MuiAutocomplete-option": { fontSize: FONT.CAPTION, minHeight: "auto", py: "4px" } } }}
      // '결과 없음' 문구는 리스트 밖(Paper 직속)이라 ListboxProps 가 닿지 않는다. 팝업은 portal 이라
      //   루트 sx 로도 못 잡으므로 Paper 슬롯에서 준다 — 기본값(14px·큰 여백)은 AI 화면 밀도에 비해 크다.
      slotProps={{ paper: { sx: {
            "& .MuiAutocomplete-noOptions": {
              fontSize: FONT.MICRO, color: C.text3, lineHeight: 1.5, p: "6px 10px" } } } }}
    />
  );
}

export default AiTargetMultiSelect;
